/**
 * Rovo Credits — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "Rovo: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129272798
 *
 * Content DRI: Belle Craig · Meter CD: Melissa Jaén
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 */

import { wacToken } from '@/libraries/design/foundations';

import type { MeterContent } from './meter-content.types';

export const ROVO_CREDITS_CONTENT: MeterContent = {
	description:
		'Get fast answers, run powerful AI agents, and connect your organizational context to any AI tool.',
	needsStep: {
		// Step 2 "Do you know your usage?" question — Rovo copy.
		doYouKnowQuestion: 'Know how many credits you\u2019ll need?',
	},
	estimateStep: {
		subtitle:
			'Based on what you\u2019ve told us, here\u2019s a summary of how many Rovo credits you need, and how you can use them.',
		compareCta: 'Explore plans',
		// TEMPORARY: All meters currently share this Orange Logic asset
		// while the content team finalizes per-meter hero imagery. Track
		// replacement in the follow-up content ticket.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb1q20l5xjp2i85j55ou317euquo3rv3.png',
			alt: '',
			imageBackgroundColor: wacToken('color.extended.blue100'),
		},
		// ContentBlock copy per Rovo Confluence spec (Step 3 right-column
		// "Compare value" panel).
		contentBlock: {
			// Copy per revised design direction (2026-07-16): reframe the
			// Card 1 messaging around ROI + built-in allowances, using
			// the "Explore plans" CTA (paired with `compareCta` above).
			title: 'Maximize your ROI with flexible, usage-based plans',
			intro:
				'With Atlassian collections and apps, built-in allowances align your costs with your real-world usage.',
			listItems: ['Optimize your spend', 'Plan with confidence', 'Do more with less'],
			href: '/collections',
		},
	},
	allowanceStep: {
		// Step 4 primary CTA — "Compare" per the latest design direction
		// (all meters share the comparison-focused verb on this step so
		// users understand the Summary step surfaces a comparison view).
		nextCta: 'Compare',
	},
	summaryStep: {
		// Step 5 header title — currently uniform across all meters per
		// spec. Kept per-meter for future flexibility (content team may
		// promote per-meter variants later, e.g. "Review your Rovo plan").
		title: 'Review usage estimate and compare',
		subtitle:
			'Based on what you\u2019ve told us, here\u2019s a summary of how many Rovo credits you need, and how you can use them.',
		// Rovo shows BOTH cards (org needs + what's covered) per
		// Figma node 2055:13212 & the Rovo Confluence spec.
		// Only the eyebrow + subtext are static — the `headline` is
		// injected at render time by summary-step.tsx from the live
		// usage-range calculation, so it's intentionally omitted here.
		// `body` and `footerNote` are omitted until content is
		// finalized (the ResultsCard renders without them).
		orgNeedsCard: {
			eyebrow: 'YOUR ORGANIZATION NEEDS',
			headlineSubtext: 'Rovo credits monthly',
		},
		// "What's covered" card (Rovo Confluence spec Step 5, Card 4).
		// This card is DYNAMIC based on whether the org's estimated usage
		// fits within its included allowance:
		//   - headline       \u2192 live included allowance (e.g. "200,000")
		//   - headlineSubtext\u2192 "Rovo credits included monthly"
		//   - covered case   \u2192 bodyCovered + listItemsCovered (value props)
		//   - overage case   \u2192 bodyOverage + listItemsOverage (guidance)
		// The `{overage}` / `{cost}` tokens in bodyOverage are replaced at
		// render time by summary-step.tsx with the live extra-usage range
		// and estimated extra monthly charge.
		whatsCoveredCard: {
			eyebrow: 'WHAT\u2019S COVERED',
			headlineSubtext: 'Rovo credits included monthly',
			bodyCovered: 'Your Rovo credits are covered within the allowance.',
			bodyOverage:
				'There\u2019s a gap of {overage} Rovo credits. You may incur extra usage charges of {cost}.',
			listItemsCovered: ['Quick answers', 'Extended tasks', 'Custom agents'],
			listItemsOverage: [
				'Higher plans provide more allowances',
				'Track your extra usage via your capabilities subscription details page in admin hub',
				'Set an extra usage limit to stay in budget',
			],
		},
		// Per revised design (2026-07-16): Rovo does NOT show the
		// Monthly Cost Estimate card. Only CSR and Assets do.
	},
	usageProfiles: {
		// Bullets (`list`) and value props (`valueProp`) per the finalized
		// Rovo Confluence spec Step 2 (Meter Usage Profiles). `usageRange`
		// is not displayed on the Step 2 cards themselves (per spec note
		// "not displayed in this step") but drives the usage estimate.
		exploring: {
			label: 'Early exploration',
			description:
				'We\u2019re testing the waters \u2014 using AI for the occasional search, summary, or draft to see where it adds value.',
			usageRange: '100\u2013500 per user per month',
			list: [
				'A few Chat searches and summaries',
				'The occasional complex research task',
				'Trying out an agent once or twice',
			],
			valueProp:
				'Teams in the early exploration profile typically save up to 2 hours per month per user.',
		},
		adopting: {
			label: 'Routine adoption',
			description:
				'AI is part of how we work each week \u2014 drafting content, catching up on updates, and automating repetitive steps across projects.',
			usageRange: '500\u20132,000 per user per month',
			list: [
				'Chat most days for quick answers',
				'Weekly research or analysis in Think Deeper',
				'Agents handling recurring tasks',
			],
			valueProp:
				'Teams in the routine adoption profile typically save 5\u20136 hours per month, per user.',
		},
		scaling: {
			label: 'Transformative usage',
			description:
				'Multiple teams rely on AI daily for planning, decision-making, and connecting knowledge across tools.',
			usageRange: '2,000\u20134,000 per user per month',
			list: [
				'AI embedded in daily workflows across teams',
				'Agents running autonomously in automations',
				'Complex research and planning on demand',
			],
			valueProp:
				'Teams in the transformative usage profile typically save 1\u20132 days per month, per user.',
		},
	},
	valueProps: [
		{
			title: 'Quick answers',
			description:
				'Get quick insights, summaries, and recommendations from Rovo Chat, without leaving your workflow.',
		},
		{
			title: 'Extended tasks',
			description:
				'Dynamically plan, search, reason, and iterate to return a thorough answer with Think deeper mode.',
		},
		{
			title: 'Custom agents',
			description:
				'Power specialized agents for tasks like grooming Jira backlogs, summarizing Confluence spaces, or drafting internal comms on behalf of specific teams.',
		},
	],
};
